import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { TotalResults } from '@shared/components/ui/TotalResults';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import type { HeaderAction } from '@shared/components/navigation/types';
import { useWallets } from '@modules/Wallets/List/hooks/useWallets';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { type ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useWithdrawalsInfinite } from '../../hooks/useWithdrawalsInfinite';
import { useWithdrawalsColumns } from '../../hooks/useWithdrawalsColumns';
import { useWithdrawalsMutations } from '../../hooks/useWithdrawalsMutations';
import { toolbarButtons } from '../../config/withdrawalStatusConfig';
import { isWithdrawalMarkableAsPaid } from '../../config/withdrawalStatusDisplay';
import { Withdrawal } from '../../models';
import { WithdrawalsAgGridTable, WithdrawalsBreadcrumb } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import {
  CreateWithdrawalButton,
  FiltersSection,
  StatusToolbar,
  WithdrawalsSelectionToolbar,
  WithdrawalsTopBarActions,
} from '../interaction';
import {
  CancelWithdrawalModal,
  CancelWithdrawalModalRef,
  CreateWithdrawalModal,
  CreateWithdrawalModalRef,
  GeneratePaymentFilesModal,
  GeneratePaymentFilesModalRef,
  MarkPaidBulkResultModal,
  MarkPaidBulkResultModalRef,
  MarkPaidWithdrawalModal,
  MarkPaidWithdrawalModalRef,
  MarkReturnedWithdrawalModal,
  MarkReturnedWithdrawalModalRef,
  RejectWithdrawalModal,
  RejectWithdrawalModalRef,
  ViewWithdrawalModal,
  ViewWithdrawalModalRef,
} from '../modals';

const getColumnId = (columnId?: string, accessorKey?: string | number) => {
  if (columnId) return columnId;
  if (typeof accessorKey === 'string') return accessorKey;
  return String(accessorKey ?? '');
};

const matchesSearch = (withdrawal: Withdrawal, term: string) => {
  if (!term) return true;
  const normalized = term.toLowerCase();

  const searchableValues = [
    withdrawal.id?.toString(),
    withdrawal.status,
    withdrawal.requested_amount?.toString(),
    withdrawal.approved_amount?.toString(),
    withdrawal.created_at,
  ];

  return searchableValues.some((value) => value?.toString().toLowerCase().includes(normalized));
};

const buildAdvancedFiltersPayload = (conditions: FilterConditionOutput[], statusFilter?: string): any | undefined => {
  const filters = conditions
    .map((condition) => {
      const { column, action } = condition;
      const rawValue = condition.value as unknown;

      if (!column || !action) {
        return null;
      }

      const MULTI_VALUE_OPERATORS = new Set(['in', 'not_in']);
      const VOID_OPERATORS = new Set(['is_null', 'is_not_null']);
      const NUMERIC_COLUMNS = new Set(['wallet_id']);

      if (VOID_OPERATORS.has(action)) {
        return {
          column,
          operator: action,
        };
      }

      if (MULTI_VALUE_OPERATORS.has(action)) {
        let valuesArray: Array<string | number> = [];

        if (Array.isArray(rawValue)) {
          valuesArray = rawValue as Array<string | number>;
        } else if (typeof rawValue === 'string') {
          valuesArray = rawValue
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
        } else if (rawValue !== undefined && rawValue !== null && rawValue !== '') {
          valuesArray = [rawValue as string | number];
        }

        if (!valuesArray.length) {
          return null;
        }

        // Convertir a números si es una columna numérica
        const processedArray = NUMERIC_COLUMNS.has(column)
          ? valuesArray
              .map((item) => {
                const numericValue = typeof item === 'number' ? item : Number(item);
                return Number.isNaN(numericValue) ? null : numericValue;
              })
              .filter((item): item is number => item !== null)
          : valuesArray;

        if (!processedArray.length) {
          return null;
        }

        return {
          column,
          operator: action,
          value: processedArray,
        };
      }

      const singleValue = Array.isArray(rawValue) ? rawValue[0] : (rawValue as string | number | undefined);

      if (singleValue === undefined || singleValue === null || singleValue === '') {
        return null;
      }

      return {
        column,
        operator: action,
        value: singleValue,
      };
    })
    .filter((filter): filter is { column: string; operator: string; value?: any } => filter !== null);

  // Añadir filtro de status si está seleccionado
  if (statusFilter) {
    filters.push({
      column: 'status',
      operator: 'in',
      value: [statusFilter],
    });
  }

  if (!filters.length) {
    return undefined;
  }

  return {
    filters,
    logic: 'AND',
  };
};

interface MainProps {
  walletIdProp?: number | null;
  embedded?: boolean;
}

export const Main = ({ walletIdProp = null, embedded = false }: MainProps) => {
  const { t } = useTranslation('withdrawalsModule');
  const { toast } = useToast();
  const navigate = useNavigate();
  const { canAccess } = usePermissions();
  const canCreate = canAccess(['tenant_external'], ['withdrawals.create']);
  const is3pl = canAccess(['tenant_3pl'], []);
  const isExternal = canAccess(['tenant_external'], []);
  const canApprove = canAccess(['tenant_3pl'], ['withdrawals.approve']);
  const canManagePaymentFiles = is3pl && canApprove;

  const { params: queryParams, updateParams: updateQueryParams } = useQueryParams({
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
    status: { type: 'string', default: '' },
    wallet_id: { type: 'number', default: 0 },
  });

  const [walletId, setWalletId] = useState<number | null>(() => {
    if (walletIdProp) return walletIdProp;
    const fromUrl = queryParams.wallet_id;
    return fromUrl && fromUrl > 0 ? fromUrl : null;
  });
  const [searchValue, setSearchValue] = useState(queryParams.search ?? '');
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(
    Array.isArray(queryParams.filters) ? queryParams.filters : [],
  );
  const [activeTab, setActiveTab] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(queryParams.status || undefined);
  const [selectedWithdrawals, setSelectedWithdrawals] = useState<Set<number>>(new Set());
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (queryParams.status) {
      setSelectedStatus(queryParams.status);
      const tabKey = toolbarButtons.find((btn) => btn.status === queryParams.status)?.key ?? 'all';
      setActiveTab(tabKey);
    }
  }, [queryParams.status]);

  useEffect(() => {
    setSearchValue(queryParams.search ?? '');
  }, [queryParams.search]);

  useEffect(() => {
    setFilterConditions(Array.isArray(queryParams.filters) ? queryParams.filters : []);
  }, [queryParams.filters]);

  useEffect(() => {
    if (embedded || walletIdProp) return;
    const urlWalletId = queryParams.wallet_id;
    if (urlWalletId && urlWalletId > 0) {
      setWalletId(urlWalletId);
    }
  }, [embedded, walletIdProp, queryParams.wallet_id]);

  const advancedFilters = useMemo(
    () => buildAdvancedFiltersPayload(filterConditions, selectedStatus),
    [filterConditions, selectedStatus],
  );

  const withdrawalsFilters = useMemo(
    () =>
      advancedFilters
        ? { advanced_filters: advancedFilters, wallet_id: walletId ?? undefined }
        : { wallet_id: walletId ?? undefined },
    [advancedFilters, walletId],
  );

  const { data, isPending, isError, error, refetch, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useWithdrawalsInfinite(walletId, withdrawalsFilters);
  const { data: walletsData, isLoading: walletsLoading } = useWallets(
    isExternal && !embedded ? { perPage: 1 } : undefined,
  );

  // Auto-seleccionar wallet para external en vista general
  useEffect(() => {
    if (!isExternal) return;
    if (embedded) return;
    if (walletId) return;
    const firstWalletId = walletsData?.data?.[0]?.id;
    if (firstWalletId) {
      setWalletId(firstWalletId);
      updateQueryParams({ wallet_id: firstWalletId });
    }
  }, [isExternal, embedded, walletId, walletsData?.data, updateQueryParams]);

  const createModalRef = useRef<CreateWithdrawalModalRef>(null);
  const cancelModalRef = useRef<CancelWithdrawalModalRef>(null);
  const markPaidModalRef = useRef<MarkPaidWithdrawalModalRef>(null);
  const rejectModalRef = useRef<RejectWithdrawalModalRef>(null);
  const markReturnedModalRef = useRef<MarkReturnedWithdrawalModalRef>(null);
  const viewModalRef = useRef<ViewWithdrawalModalRef>(null);
  const generatePaymentFilesModalRef = useRef<GeneratePaymentFilesModalRef>(null);
  const markPaidBulkResultModalRef = useRef<MarkPaidBulkResultModalRef>(null);
  const { markPaidBulk, generatePaymentFiles } = useWithdrawalsMutations();

  const handleOpenCreate = useCallback(() => {
    if (!walletId) return;
    createModalRef.current?.open(walletId);
  }, [walletId]);

  const handleCancel = useCallback((w: Withdrawal) => {
    cancelModalRef.current?.open(w);
  }, []);

  const handleMarkPaid = useCallback((w: Withdrawal) => {
    markPaidModalRef.current?.open(w);
  }, []);

  const handleReject = useCallback((w: Withdrawal) => {
    rejectModalRef.current?.open(w);
  }, []);

  const handleMarkReturned = useCallback((w: Withdrawal) => {
    markReturnedModalRef.current?.open(w);
  }, []);

  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  const { tanStackColumns, filteredColumnDefs } = useWithdrawalsColumns({
    onMarkPaid: is3pl ? handleMarkPaid : undefined,
    onReject: is3pl ? handleReject : undefined,
    onCancel: isExternal ? handleCancel : undefined,
    onMarkReturned: is3pl ? handleMarkReturned : undefined,
    onViewDetail: (w) => viewModalRef.current?.open(w),
    selectedColumns,
  });

  const columnIds = useMemo(
    () =>
      tanStackColumns.map((column) =>
        getColumnId(
          column.id as string,
          Object.prototype.hasOwnProperty.call(column, 'accessorKey')
            ? (column as { accessorKey?: string | number }).accessorKey
            : undefined,
        ),
      ),
    [tanStackColumns],
  );

  useEffect(() => {
    setSelectedColumns((current) => {
      if (current.length === columnIds.length && current.every((id) => columnIds.includes(id))) {
        return current;
      }
      return columnIds;
    });
  }, [columnIds]);

  const handleSearch = useCallback(
    (value: string) => {
      setSearchValue(value);
      if (queryParams.search !== value) {
        updateQueryParams({ search: value });
      }
    },
    [queryParams.search, updateQueryParams],
  );

  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      setFilterConditions(conditions);
      const currentFilters = Array.isArray(queryParams.filters) ? queryParams.filters : [];
      if (JSON.stringify(currentFilters) !== JSON.stringify(conditions)) {
        updateQueryParams({ filters: conditions });
      }
    },
    [queryParams.filters, updateQueryParams],
  );

  const handleColumnsChange = useCallback((columnsIds: string[]) => {
    setSelectedColumns(columnsIds);
  }, []);

  const handleStatusChange = useCallback(
    (status?: string) => {
      setSelectedStatus(status);
      updateQueryParams({ status: status || '' });
    },
    [updateQueryParams],
  );

  const handleSelectionChanged = useCallback((selectedIds: Set<number>) => {
    setSelectedWithdrawals(selectedIds);
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedWithdrawals(new Set());
  }, []);

  const handleOpenGeneratePaymentFiles = useCallback(() => {
    generatePaymentFilesModalRef.current?.open();
  }, []);

  const handleViewPaymentFiles = useCallback(() => {
    navigate('/withdrawal-payment-files');
  }, [navigate]);

  const headerActions = useMemo<HeaderAction[]>(() => {
    if (!canManagePaymentFiles || embedded) return [];

    return [
      {
        id: 'withdrawals-payment-files-actions',
        label: '',
        onClick: () => {},
        component: (
          <WithdrawalsTopBarActions
            onGeneratePaymentFiles={handleOpenGeneratePaymentFiles}
            onViewPaymentFiles={handleViewPaymentFiles}
            isGenerating={generatePaymentFiles.isPending}
          />
        ),
      },
    ];
  }, [
    canManagePaymentFiles,
    embedded,
    handleOpenGeneratePaymentFiles,
    handleViewPaymentFiles,
    generatePaymentFiles.isPending,
  ]);

  const withdrawals = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data]);

  const selectedWithdrawalsData = useMemo(
    () => withdrawals.filter((w) => selectedWithdrawals.has(w.id)),
    [withdrawals, selectedWithdrawals],
  );

  const markableCount = useMemo(
    () => selectedWithdrawalsData.filter((w) => isWithdrawalMarkableAsPaid(w.status)).length,
    [selectedWithdrawalsData],
  );

  const handleMarkPaidBulk = useCallback(async () => {
    const ids = selectedWithdrawalsData.filter((w) => isWithdrawalMarkableAsPaid(w.status)).map((w) => w.id);
    if (!ids.length) return;

    try {
      console.log('🔍 Main - Mark paid bulk:', { ids });
      const response = await markPaidBulk.mutateAsync({ ids });

      // Normalizar respuesta: puede venir envuelta en data o como array directo
      const results = Array.isArray(response) ? response : ((response as any)?.data ?? []);
      markPaidBulkResultModalRef.current?.open(results);
      handleClearSelection();
      refetch();
    } catch (error) {
      console.log('🔍 Main - Mark paid bulk error:', error);
      toast({
        title: t('errors.markPaidFailed'),
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  }, [selectedWithdrawalsData, markPaidBulk, handleClearSelection, refetch, toast, t]);

  const totalFromApi = data?.pages[0]?.meta.total;

  const filteredWithdrawals = useMemo(
    () => withdrawals.filter((w) => matchesSearch(w, searchValue)),
    [withdrawals, searchValue],
  );

  const hasActiveFilters = Boolean(searchValue?.trim()) || filterConditions.length > 0;
  // Mismo criterio que Wallets/List (AgGrid + infinite): atenuar tabla mientras llega la respuesta, sin bloquear UI.
  const isRefetching = isFetching && !isPending && !isFetchingNextPage;

  const isBootstrapping = isPending || (isExternal && !embedded && walletsLoading);

  useEffect(() => {
    if (!isBootstrapping && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isBootstrapping, isInitialLoad]);

  // Spinner solo en la carga inicial (mismo patrón que Orders/List)
  if (isInitialLoad) {
    return <LoadingState />;
  }

  if (isError || !data) {
    return <ErrorState error={error ?? new Error(t('errors.generic'))} onRetry={refetch} title={t('errors.title')} />;
  }

  return (
    <>
      <WithdrawalsBreadcrumb headerActions={headerActions} />
      <div className="relative flex min-h-0 flex-1 flex-col gap-4 px-6 pb-6 pt-4">
        <div className="pointer-events-none absolute right-6 top-4 z-[5] flex items-center gap-2">
          {canCreate && walletId ? (
            <span className="pointer-events-auto">
              <CreateWithdrawalButton onClick={handleOpenCreate} />
            </span>
          ) : null}
        </div>

        {/* Filtros + tabs: mismo patrón que Mercancía (shrink-0 para que no quede tapado por el empty) */}
        <div className="relative z-[6] shrink-0 space-y-4 bg-background pb-px">
          <div
            className={`relative flex flex-wrap items-center justify-between gap-3 ${canCreate && walletId ? 'pr-40 sm:pr-44' : ''}`}
          >
            <FiltersSection
              columns={tanStackColumns as ColumnDef<Withdrawal>[]}
              selectedColumns={selectedColumns}
              fixedColumns={['actions']}
              onColumnsChange={handleColumnsChange}
              searchValue={searchValue}
              onSearch={handleSearch}
              filterConditions={filterConditions}
              onFilterChange={handleFilterChange}
            />

            <div className="flex flex-col items-end ml-auto">
              <TotalResults
                total={searchValue?.trim() ? filteredWithdrawals.length : (totalFromApi ?? filteredWithdrawals.length)}
              />
            </div>
          </div>
          <StatusToolbar onStatusChange={handleStatusChange} active={activeTab} setActive={setActiveTab} />
        </div>

        {/* AgGrid: con 0 filas el grid colapsa a h=0 — min-height evita que el EmptyView sea absolut y desborde encima de las tabs */}
        <div
          className={`relative isolate z-0 flex min-h-0 flex-1 flex-col overflow-hidden ${
            filteredWithdrawals.length === 0 ? 'min-h-[min(420px,calc(100vh-20rem))]' : ''
          }`}
        >
          <div
            className={`min-h-0 flex-1 flex flex-col transition-opacity duration-300 ${
              isRefetching ? 'opacity-50 pointer-events-none' : 'opacity-100'
            }`}
          >
            <WithdrawalsAgGridTable
              withdrawals={filteredWithdrawals}
              columnDefs={filteredColumnDefs}
              enableSelection={canManagePaymentFiles}
              selectedWithdrawals={selectedWithdrawals}
              onSelectionChanged={canManagePaymentFiles ? handleSelectionChanged : undefined}
              fetchNextPage={() => {
                void fetchNextPage();
              }}
              hasNextPage={hasNextPage ?? false}
              isFetchingNextPage={isFetchingNextPage}
            />
          </div>
          {filteredWithdrawals.length === 0 && (
            <div className="absolute inset-0 z-[1] flex items-center justify-center bg-background/90">
              <EmptyView
                type={hasActiveFilters ? 'noResults' : 'emptyState'}
                icon={EMPTY_VIEW_ICONS.ORDERS}
                title={hasActiveFilters ? t('noResults') : t('emptyState')}
              />
            </div>
          )}
        </div>
      </div>

      {canManagePaymentFiles && (
        <WithdrawalsSelectionToolbar
          selectedCount={selectedWithdrawals.size}
          markableCount={markableCount}
          onClose={handleClearSelection}
          onMarkPaidBulk={handleMarkPaidBulk}
        />
      )}

      <CreateWithdrawalModal ref={createModalRef} onSuccess={refetch} />
      <CancelWithdrawalModal ref={cancelModalRef} onSuccess={refetch} />
      <MarkPaidWithdrawalModal ref={markPaidModalRef} onSuccess={refetch} />
      <RejectWithdrawalModal ref={rejectModalRef} onSuccess={refetch} />
      <MarkReturnedWithdrawalModal ref={markReturnedModalRef} onSuccess={refetch} />
      <ViewWithdrawalModal ref={viewModalRef} />
      <GeneratePaymentFilesModal ref={generatePaymentFilesModalRef} onSuccess={refetch} />
      <MarkPaidBulkResultModal ref={markPaidBulkResultModalRef} />
    </>
  );
};
